import type { ClientMetadata, ClientTrackRequestBody, EventData } from "~/lib/fpd/schemas";
import { v4 as uuid } from "uuid";

const LAST_PAGE_URL_SESSION_KEY = "last_page_url";
const THIS_PAGE_URL_SESSION_KEY = "this_page_url";

declare global {
    interface Window {
        FPD_CONSENT?: boolean;
    }
}

function getReferrerUrl(): string | undefined {
    const lastPageUrl = sessionStorage.getItem(LAST_PAGE_URL_SESSION_KEY);
    return lastPageUrl?.length ? lastPageUrl : undefined;
}

function setReferrerUrl() {
    const lastPage = sessionStorage.getItem(THIS_PAGE_URL_SESSION_KEY) ?? document.referrer;
    sessionStorage.setItem(LAST_PAGE_URL_SESSION_KEY, lastPage);
    sessionStorage.setItem(THIS_PAGE_URL_SESSION_KEY, location.href);
}

function getClientMetadata(): ClientMetadata {
    return {
        browser_height: window.innerHeight,
        browser_language: navigator.language,
        browser_width: window.innerWidth,
        device_created: new Date().toISOString(),
        device_screen_height: window.screen.height,
        device_screen_width: window.screen.width,
        device_sent: new Date().toISOString(),
        event_id: uuid(),
        os_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        page_title: document.title,
        page_url: window.location.href,
        referrer_url: getReferrerUrl(),
        user_agent: window.navigator.userAgent,
    };
}

function isBot(): boolean {
    return /bot|crawl|spider/i.test(window.navigator.userAgent);
}

async function getConsent() {
    for (let attempts = 0; attempts < 10; attempts++) {
        if (window.FPD_CONSENT) {
            return true;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => {
            setTimeout(r, 100);
        });
    }
    return false;
}

async function track(data: EventData) {
    if (isBot() || !window.ENV.FPD_ENABLED || !(await getConsent())) {
        return;
    }
    const body: ClientTrackRequestBody = {
        client_metadata: getClientMetadata(),
        event_data: data,
    };
    await fetch("/college-recruiting/track", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
    });
}

export function trackClick(id: string, tag: string, text: string) {
    void track({
        event_type: "click",
        event_click_id: id,
        event_click_tag: tag,
        event_click_text: text,
    });
}

export function trackPageView() {
    setReferrerUrl();
    void track({
        event_type: "view",
        event_view_type: "page",
    });
}

export function trackCustomEvent(name: string, values: Record<string, string>) {
    void track({
        event_type: "custom",
        event_custom_type: name,
        event_custom_values: values,
    });
}
